import { useEffect, useRef } from 'react';
import { FormikProps } from 'formik';

/**
 * A custom hook that automatically scrolls the viewport to the first input field
 * containing an error when a user attempts to submit a Formik form but fails validation.
 * 
 * @param formik - The formik instance object (or subset) containing submitCount and errors.
 */
export const useScrollToError = <T extends Record<string, any>>(
  formik: Pick<FormikProps<T>, 'errors' | 'submitCount' | 'isSubmitting'>
) => {
  const { errors, submitCount, isSubmitting } = formik;
  const prevSubmitCount = useRef(submitCount);

  useEffect(() => {
    // Formik increments submitCount on every submit attempt.
    // If submitCount increases but there are errors (preventing actual submission),
    // we want to scroll the user to the first error.
    if (submitCount > prevSubmitCount.current && !isSubmitting && Object.keys(errors).length > 0) {
      // Small timeout allows React to re-render and apply .Mui-error CSS classes
      const timer = setTimeout(() => {
        // Find the first Material-UI element flagged with an error state
        const errorElement = document.querySelector('.Mui-error, .tab-warning, [aria-invalid="true"]');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Optional: Add a brief highlight effect to draw the eye
          if (errorElement instanceof HTMLElement) {
            const originalOutline = errorElement.style.outline;
            const originalTransition = errorElement.style.transition;
            errorElement.style.transition = 'outline 0.3s ease';
            errorElement.style.outline = '2px solid rgba(211, 47, 47, 0.5)';
            errorElement.style.outlineOffset = '4px';
            setTimeout(() => {
              errorElement.style.outline = originalOutline;
              setTimeout(() => {
                errorElement.style.transition = originalTransition;
              }, 300);
            }, 1500);
          }
        }
      }, 100);
      
      prevSubmitCount.current = submitCount;
      return () => clearTimeout(timer);
    }
    
    // Catch-up ref if it updated without errors
    if (submitCount > prevSubmitCount.current) {
        prevSubmitCount.current = submitCount;
    }
  }, [submitCount, isSubmitting, errors]);
};

export default useScrollToError;
